<template>
  <v-container class="col-md-10 px-lg-5 bd-content py-12" role="main">
    <h1 class="bd-title" id="content">{{ $t('title') }}</h1>
    <p class="mt-6">
      {{ $t('intro.p1') }}
      {{ $t('intro.p2') }}
    </p>

    <p>{{ $t('source') }}</p>
    <br>

    <p><strong>{{ $t('sections.identification.title') }}</strong></p>
    <p>{{ $t('sections.identification.text') }}</p>
    <blockquote>
      <p>
        {{ $t('sections.identification.details.name') }}<br>
        {{ $t('sections.identification.details.commercial') }}<br>
        {{ $t('sections.identification.details.cif') }}<br>
        {{ $t('sections.identification.details.address') }}<br>
      </p>
    </blockquote>

    <p><strong>{{ $t('sections.communications.title') }}</strong></p>
    <p>{{ $t('sections.communications.text') }}</p>
    <p>
      {{ $t('sections.communications.contact.email') }}<br>
      {{ $t('sections.communications.contact.address') }}<br>
      {{ $t('sections.communications.contact.note') }}
    </p>

    <p><strong>{{ $t('sections.communications.marketing.title') }}</strong></p>
    <p>{{ $t('sections.communications.marketing.text') }}</p>

    <p><strong>{{ $t('sections.services.title') }}</strong></p>
    <p>{{ $t('sections.services.description') }}</p>
    <p>{{ $t('sections.services.rights.p1') }}</p>
    <p>{{ $t('sections.services.rights.p2') }}</p>
    <p>{{ $t('sections.services.rights.p3') }}</p>
    <p>{{ $t('sections.services.rights.p4') }}</p>

    <p><strong>{{ $t('sections.availability.title') }}</strong></p>
    <p>{{ $t('sections.availability.access') }}</p>
    <p>
      {{ $t('sections.availability.unavailable.p1') }}<br>
      {{ $t('sections.availability.unavailable.p2') }}
    </p>

    <p><strong>{{ $t('sections.purchaseProcess.title') }}</strong></p>
    <p>
      {{ $t('sections.purchaseProcess.availability') }}<br>
      {{ $t('sections.purchaseProcess.requirements.age') }}<br>
      {{ $t('sections.purchaseProcess.requirements.registration') }}
    </p>
    <p>{{ $t('sections.purchaseProcess.steps.title') }}</p>
    <ul>
      <li v-for="(step, index) in $t('sections.purchaseProcess.steps.list')" :key="index">
        {{ step }}
      </li>
      <ul>
        <li>{{ $t('sections.purchaseProcess.steps.confirmation') }}</li>
      </ul>
    </ul>
    <p>
      <span class="title">{{ $t('sections.purchaseProcess.billing.title') }}</span><br>
      {{ $t('sections.purchaseProcess.billing.text') }}
    </p>

    <p><strong>{{ $t('sections.delivery.title') }}</strong></p>
    <p>{{ $t('sections.delivery.zones') }}</p>
    <p><strong>{{ $t('sections.delivery.formsAndTerms.title') }}</strong></p>
    <p>{{ $t('sections.delivery.formsAndTerms.description') }}</p>
    <p>{{ $t('sections.delivery.formsAndTerms.schedule') }}</p>
    <p>{{ $t('sections.delivery.formsAndTerms.processing') }}</p>

    <ul>
      <li><strong>{{ $t('sections.delivery.highSeason.title') }}</strong>: 
        {{ $t('sections.delivery.highSeason.text') }}
      </li>
    </ul>

    <li>{{ $t('sections.delivery.homeDelivery.title') }}
      <ul>
        <li>
          <p><strong>{{ $t('sections.delivery.homeDelivery.schedule') }}</strong></p>
        </li>
        <li>
          <p><strong>{{ $t('sections.delivery.holidays.title') }}</strong>: 
            {{ $t('sections.delivery.holidays.text') }}
          </p>
        </li>
        <li v-for="(service, index) in $t('sections.delivery.homeDelivery.services')" 
            :key="index">
          {{ service }}
        </li>
      </ul>
    </li>

    <p><strong>{{ $t('sections.delivery.observations.title') }}</strong></p>
    <ul>
      <li>
        <strong>{{ $t('sections.delivery.observations.agencies.title') }}</strong><br>
        {{ $t('sections.delivery.observations.agencies.text') }}
      </li>
      <li>
        <strong>{{ $t('sections.delivery.observations.schedules.title') }}</strong><br>
        {{ $t('sections.delivery.observations.schedules.text') }}
      </li>
      <li>
        <strong>{{ $t('sections.delivery.observations.delays.title') }}</strong><br>
        {{ $t('sections.delivery.observations.delays.text') }}
      </li>
      <li>
        <strong>{{ $t('sections.delivery.observations.incorrectAddress.title') }}</strong><br>
        {{ $t('sections.delivery.observations.incorrectAddress.text') }}<br>
        {{ $t('sections.delivery.observations.incorrectAddress.consequences') }}
      </li>
    </ul>

    <strong>{{ $t('sections.pricing.title') }}</strong>
    <p>{{ $t('sections.pricing.general') }}</p>
    <p>{{ $t('sections.pricing.details') }}</p>
    <p>
      {{ $t('sections.pricing.tax.included') }}<br>
      {{ $t('sections.pricing.tax.rate') }}<br>
      {{ $t('sections.pricing.shipping.info') }}
      {{ $t('sections.pricing.shipping.free') }}
    </p>

    <p><strong>{{ $t('sections.payment.title') }}</strong></p>
    <blockquote>
      <p>
        <strong>{{ $t('sections.payment.creditCard.title') }}</strong><br>
        {{ $t('sections.payment.creditCard.description') }}
        {{ $t('sections.payment.creditCard.refund') }}
      </p>
    </blockquote>

    <p><strong>{{ $t('sections.returns.title') }}</strong></p>
    <p>{{ $t('sections.returns.legal') }}</p>

    <ul>
      <li>
        <strong>{{ $t('sections.returns.types.withdrawal.title') }}:</strong> 
        {{ $t('sections.returns.types.withdrawal.description') }}
      </li>
      <li>
        <strong>{{ $t('sections.returns.types.defective.title') }}:</strong> 
        {{ $t('sections.returns.types.defective.description') }}
      </li>
    </ul>

    <strong>{{ $t('sections.returns.generalInfo.title') }}:</strong>
    <br>
    <p>
      {{ $t('sections.returns.generalInfo.deadline') }}<br>
      {{ $t('sections.returns.generalInfo.suspension') }}<br>
      {{ $t('sections.returns.generalInfo.confirmation') }}<br>
      {{ $t('sections.returns.generalInfo.refund') }}<br>
      {{ $t('sections.returns.generalInfo.contact') }}
    </p>

    <p><strong>{{ $t('sections.returns.orderCancellation.title') }}</strong></p>
    <p>{{ $t('sections.returns.orderCancellation.timeLimit') }}</p>
    <p>{{ $t('sections.returns.orderCancellation.process') }}</p>
    <p><strong>{{ $t('sections.returns.orderCancellation.restriction') }}</strong></p>

    <p><strong>{{ $t('sections.responsibility.title') }}</strong></p>
    <p>
      {{ $t('sections.responsibility.privacy') }}
      <router-link to="/politica-de-privacitat">Política de Privacitat</router-link>
    </p>
    <p>{{ $t('sections.responsibility.serviceChanges') }}</p>

    <p><strong>{{ $t('sections.customerService.title') }}</strong></p>
    <p>
      {{ $t('sections.customerService.contact') }}
      <router-link to="/contacto">d'atenció al client</router-link>
    </p>
    <ul>
      <li>{{ $t('sections.customerService.requirements') }}</li>
    </ul>
    <br>
    <strong>{{ $t('sections.customerService.onlineDisputes.title') }}</strong>
    <br>
    {{ $t('sections.customerService.onlineDisputes.text') }}
    <a href="https://ec.europa.eu/consumers/odr" 
       target="_blank" 
       rel="nofollow">http://ec.europa.eu/consumers/odr/</a>

    <p><strong>{{ $t('sections.legislation.title') }}</strong></p>
    <p>
      {{ $t('sections.legislation.law') }}<br>
      {{ $t('sections.legislation.language') }}
    </p>

  </v-container>
</template>

<script>
export default {
  name: "CondicionesGenerales",
  data() {
    return {
      translations: {
        ca: {
          title: "Condicions Generals",
          intro: {
            p1: "L'adquisició de qualssevol dels serveis comporta l'acceptació plena i sense reserves de totes i cadascuna de les Condicions Generals que s'indiquen, sense perjudici de l'acceptació de les Condicions Particulars que podrien ser aplicable en adquirir determinats serveis.",
            p2: "Aquestes Condicions Generals podran ser modificades sense notificació prèvia, per tant, és recomanable llegir atentament el seu contingut abans de procedir a l'adquisició de qualsevol dels serveis oferts."
          },
          source: "La font dels exàmens són continguts editats provinents de la Generalitat de Catalunya. Departament d'Economia i Coneixement. Consell Interuniversitari de Catalunya (CIC)",
          sections: {
            identification: {
              title: "1. IDENTIFICACIÓ",
              text: "Sele.ai, en compliment de la Llei 34/2002, d'11 de juliol, de Serveis de la Societat de la Informació i de Comerç Electrònic, l'informa que:",
              details: {
                name: "La seva denominació social és: Paꜱqual Maꜱ.",
                commercial: "El seu nom comercial és: Sele.ai",
                cif: "El seu CIF és: 47998845F.",
                address: "El seu domicili social està en: Pg. Gracia, 118, 08008 - Barcelona , (Espanya)."
              }
            },
            communications: {
              title: "2. COMUNICACIONS",
              text: "Per a comunicar-se amb nosaltres, posem a la seva disposició diferents mitjans de contacte que detallem a continuació:",
              contact: {
                email: "Email: hola@sele.ai",
                address: "Adreça postal: Pg. Gracia, 118, 08008 - Barcelona, (Espanya).",
                note: "Totes les notificacions i comunicacions entre els usuaris i Examenselectivitat es consideraran eficaços, amb caràcter general, quan es realitzin a través de qualsevol mitjà dels detallats anteriorment.",
                marketing: {
                  title: "Comunicacions comercials",
                  text: "En acceptar aquestes condicions generals, autoritzes expressament a Sele.ai per enviar-te comunicacions comercials personalitzades per correu electrònic sobre productes i serveis que puguin ser del teu interès, basant-nos en el teu historial de compres i preferències. Pots revocar aquest consentiment en qualsevol moment contactant amb nosaltres a hola@sele.ai."
                }
              }
            },
            services: {
              title: "3. SERVEIS",
              description: "Els serveis oferts per Examenselectivitat consisteixen en la realització d'impressió de documents o la venta de software com a servei, amb la configuració particular i característiques indicades en el procés de compra. En el cas dels dossiers, el client obtindrà com a resultat la impressió sobre paper o altres suports, amb el lliurament en domicili a la direcció mencionada en el procés de compra.",
              rights: {
                p1: "Examenselectivitat es reserva el dret de decidir, a cada moment, els productes i /o serveis que s'ofereixen als Clients. D'aquesta manera, Examenselectivitat podrà, en qualsevol moment, afegir nous productes i /o serveis als oferts actualment.",
                p2: "Així mateix Examenselectivitat es reserva el dret a retirar o deixar d'oferir, en qualsevol moment, i sense previ avís, qualssevol dels serveis oferts, sense que això afecti els serveis contractats la prestació dels quals no hagi finalitzat.",
                p3: "Tot això sense perjudici que l'adquisició d'algun dels serveis només podrà fer-se mitjançant la subscripció o registre de l'USUARI, el nom d'Usuari del qual serà el seu correu electrònic que portarà associada una contrasenya, els quals li identificaran i habilitaran personalment per a poder tenir accés als serveis.",
                p4: "Una vegada dins d'https://sele.ai, i per a accedir a la contractació dels diferents serveis, l'Usuari haurà de seguir totes les instruccions indicades en el procés de compra, la qual cosa suposarà la lectura i acceptació de totes les condicions generals i particulars fixades en https://sele.ai"
              }
            },
            availability: {
              title: "4. DISPONIBILITAT I SUBSTITUCIONS",
              access: "Per a la prestació de serveis, Examenselectivitat, donarà accés de la següent forma: creant el client un compte i realitzant l'encàrrec a través de la web seguint els passos indicats en el procés de compra. El Client rebrà correus sobre l'enviament i se l'informarà totes les dades necessàries per saber l'estat en el que es troba la comanda.",
              unavailable: {
                p1: "En cas que, per qualsevol motiu, Examenselectivitat no pugui realitzar la impressió i, per tant, no pugui lliurar-se l'encàrrec al client, se l'informarà al més aviat possible i es realitzarà la devolució de l'import pagat per aquest producte concret.",
                p2: "Examenselectivitat no es responsabilitza dels danys o perjudicis que puguin produir-se a aquest USUARI conseqüència de fallades o desconnexions en les xarxes de telecomunicacions i que ocasionin suspensió, cancel·lació o interrupció del servei d'https://sele.ai durant la prestació d'aquest."
              }
            },
            purchaseProcess: {
              title: "5. PROCÉS DE COMPRA I/O CONTRACTACIÓ",
              availability: "La recepció de les comandes i la compra en línia, pot realitzar-se en qualsevol moment, les 24h. del dia els 365 dies de l'any.",
              requirements: {
                age: "Per a realitzar qualsevol compra a https://sele.ai, és necessari que el client sigui major d'edat.",
                registration: "A més caldrà que el client es registri a https://sele.ai a través d'un formulari de recollida de dades en el qual es proporcioni a Examenselectivitat la informació necessària per a la contractació; dades que en qualsevol cas seran veraços, exactes i complets sobre la seva identitat i que el client haurà de consentir expressament mitjançant l'acceptació de la política de privacitat de Examenselectivitat."
              },
              steps: {
                title: "Els passos a seguir per a realitzar la compra són:",
                list: [
                  "Afegeixi a la cistella tants productes com desitgi.",
                  "Vagi a la cistella i ompli la informació necessària per a calcular l'enviament",
                  "Indiqui la forma en què desitja rebre la comanda i realitzi el pagament."
                ],
                confirmation: "Al dur a terme la comanda, se li enviarà des de hola@sele.ai un correu amb tota la informació de la comanda que serveix com a comprovant."
              },
              billing: {
                title: "FACTURACIÓ",
                text: "Si desitja rebre factura, sol·liciti-ho enviant un correu electrònic a hola@sele.ai"
              }
            },
            delivery: {
              title: "6. LLIURAMENT",
              zones: "Les zones geogràfiques de lliurament són: Espanya Peninsular, Balears, Portugal Peninsular i enviaments a Europa",
              formsAndTerms: {
                title: "FORMES I TERMINIS DE LLIURAMENT",
                description: "La forma de lliurament es tria pel client en el procés de compra. Els terminis de lliurament variaran segons la modalitat triada i destí.",
                schedule: "Totes les comandes formalitzades i pagats, que es rebin i completin fins a les 12.00 hores d'un dia laborable (Dilluns a Divendres),",
                processing: "es procuraran imprimir i lliurar a l'agència de transport en el mateix dia, tenint preferència aquelles comandes contractades com a Serveis de Lliurament EXPRESS"
              },
              highSeason: {
                title: "TEMPORADA ALTA",
                text: "En èpoques de màxima demanda (inici del curs escolar \"setembre-octubre\", nadal, exàmens, canvi de quadrimestre, \"febrer\", o convocatòries d'oposicions, el temps de fabricació i enviament es pot veure incrementat en 24h. més del tipus d'enviament triat, exceptuant els tipus de Enviament Express, que es fa el possible per a complir els terminis."
              },
              homeDelivery: {
                title: "Enviaments i lliurament de comandes \"A domicili\"",
                schedule: "Els enviaments amb lliuraments en domicili, es realitzen de Dilluns a Divendres, en dies laborables.",
                holidays: {
                  title: "FESTIVITATS",
                  text: "Els dissabtes, diumenges i festius, no es fan lliuraments. Festivitats Locals, Regionals o Nacionals, tant d'Origen com de Destinació, tampoc es fan recollides, ni lliuraments per part del transport, per la qual cosa no es comptabilitzen com a dia laborable."
                },
                services: [
                  "Servei de lliurament a domicili Espanya Peninsular - Servei realitzat per empreses de transport independent.",
                  "Servei de lliurament a domicili Balears - Servei realitzat per empreses de transport independent, tenint en compte les indicacions en l'apartat AGÈNCIES",
                  "Per a lliuraments en poblacions petites, distants de l'agència de transport, està podria retardar-se *24h., en casos excepcionals."
                ]
              },
              observations: {
                title: "OBSERVACIONS SOBRE EL LLIURAMENT",
                agencies: {
                  title: "AGÈNCIES",
                  text: "L'enviament dels nostres productes es realitza per agències de transport independent l'efectivitat de lliurament del qual als nostres clients en el temps acordat és superior al 93%. Els enviaments són lliurats en l'adreça del Destinatari facilitada com a <adreça d'enviament> aquesta ha de ser completa i clara, amb totes les dades necessàries i exactes pel Remitent, Telèfon nacional, Contacte, adreça, núm. portal, escala, pisos, portes, etc."
                },
                schedules: {
                  title: "HORARIS de LLIURAMENTS",
                  text: "Els horaris per als lliuraments en dia laborable no podran ser fixats amb anterioritat, ni exigibles per l'usuari."
                },
                delays: {
                  title: "RETARDS EN EL LLIURAMENT",
                  text: "En cas de retards en el lliurament, el client en cap cas podrà exigir la devolució íntegra o parcial de la comanda realitzada o les despeses d'enviament. Així mateix s'informa els clients que en èpoques nadalenques, campanyes de venda en línia com Blackfriday, lliurament en horaris no concertats, campanya d'estiu, per condicions climatològiques adverses, corts o accessos restringits, tant en la zona de lliurament, com en qualsevol de les rutes/enllacis, que el paquet sofreixi o requereixi per a arribar des d'origen a destinació, etc. o similar, poden produir-se retards o demores en els lliuraments."
                },
                incorrectAddress: {
                  title: "DIRECCIÓ INCOMPLETA, INCORRECTA, ABSÈNCIA REITERADA",
                  text: "Si per motivos de dirección incompleta, incorrecta, ausente en varios intentos de entrega (2 exactamente), o rechazo del producto, se generaría devolución de mercancía, o esta tendría que ser enviada de nuevo a esta o otra dirección la <recanalización> tendrá que ser abonada por el cliente.",
                  consequences: "La imposibilidad de entregar el producto de manera definitiva por causas imputables al cliente por los motivos indicados anteriormente, y provocar un retorno a nuestras instalaciones, NO dará derecho al cliente a reclamar el importe abonado por el trabajo encomendado, salvo que demuestre que la falta de entrega se ha debido a causas ajenas a su voluntad, situación extraordinaria o fuerza mayor. El cliente tiene un plazo de 30 días desde la devolución de la encomienda, para abonar un nuevo envío y se enviará de nuevo, o pasado este tiempo, la encomienda pasará a destrucción y NO podrá reclamar importe alguno."
                }
              }
            },
            pricing: {
              title: "7. PREUS, DESPESES I IMPOSTOS",
              general: "Els preus dels serveis que es recullen en https://sele.ai vindran expressats en Euros tret que s'expressi el contrari i estaran garantits durant el temps de validesa dels mateixos i sigui com sigui el destí de la comanda.",
              details: "Els preus finals de tots els serveis, al costat de les seves característiques, les ofertes, descomptes, etc. es reflecteixen en cadascun dels serveis que s'ofereixen en https://sele.ai",
              tax: {
                included: "Els serveis tenen l'IVA inclòs.",
                rate: "El tipus d'IVA és del 21%."
              },
              shipping: {
                info: "Despeses d'enviament: estan informats en el lloc web, segons la modalitat de lliurament triat i la quantitat de productes seleccionats.",
                free: "Enviaments gratuïts per a la Peninsula per a compres superiors de 4 dossiers."
              }
            },
            payment: {
              title: "8. FORMES DE PAGAMENT",
              creditCard: {
                title: "Targeta de crèdit o dèbit",
                description: "Pot realitzar el pagament de manera immediata a través de la passarel·la de pagament segur alimentada per la companyia Stripe.",
                refund: "Si fos necessari realitzar qualsevol devolució o cancel·lació de la comanda, l'import es reemborsarà a través de la mateixa targeta de pagament (tardança 5-10 dies)."
              }
            },
            returns: {
              title: "9. POLÍTICA DE DEVOLUCIONS I DESISTIMENT",
              legal: "Segons la legislació espanyola i europea (Llei General per a la Defensa dels Consumidors i Usuaris i altres lleis complementàries, RD 1/2007), hi ha algunes pautes generals:",
              types: {
                withdrawal: {
                  title: "Dret de desistiment",
                  description: "Si el consumidor exerceix el dret de desistiment (és a dir, retorna el producte simplement perquè ha canviat d'opinió), les despeses de devolució són a càrrec del consumidor."
                },
                defective: {
                  title: "Productes defectuosos o incorrectes",
                  description: "Si la devolució es deu a un defecte del producte o a un error en l'enviament. Es tornarà a enviar de nou i totes les despeses es faràn càrrecs d'Examenselectivitat."
                }
              },
              generalInfo: {
                title: "Informació general",
                deadline: "El termini per a desistir del contracte de prestació de serveis és de 14 dies naturals des del dia de la celebració del contracte, devent l'usuari posar en coneixement de Examenselectivitat el seu desig d'exercitar tal dret, la qual cosa podrà fer emplenant el formulari que es troba al final d'aquest document.",
                suspension: "L'exercici del dret de desistiment dona lloc a la suspensió del servei.",
                confirmation: "Examenselectivitat comunicarà al consumidor en suport durador un justificant de recepció d'aquest desistiment.",
                refund: "El desistiment implica que, Examenselectivitat procedirà a la devolució de l'import ja abonat pel client en un termini màxim de 14 dies naturals, seguint el mateix procediment triat pel client per al seu abonament (sempre que el servei contractat no estigui dins de les excepcions indicades anteriorment).",
                contact: "Una vegada rebem la seva sol·licitud ens posarem en contacte amb el client per a indicar-li els detalls de la devolució."
              },
              orderCancellation: {
                title: "ANULACIÓ DE COMANDES i COMANDES EN PROCÉS",
                timeLimit: "Pot anul·lar la comanda mentre la recepció de la comanda i la comunicació d'anul·lació no hagin transcorregut més de 3 hores",
                process: "Es pot sol·licitar per correu a hola@sele.ai i el reemborsament serà retornat a la mateixa targeta bancària amb la qual s'havia dut a terme el pagament.",
                restriction: "No es podrà anul·lar la comanda, si ha rebut un correu expressant que la seva comanda està de camí."
              }
            },
            responsibility: {
              title: "10. RESPONSABILITAT I EXONERACIÓ DE RESPONSABILITAT",
              privacy: "Així mateix Examenselectivitat compleix amb la normativa vigent de protecció de dades. Disposa d'informació ampliada en la nostra Política de Privacitat",
              serviceChanges: "Examenselectivitat podrà suspendre, retirar o cancel·lar parcial o totalment els serveis sotmesos a contractació en qualsevol moment i sense necessitat d'avís previ. La previsió anterior no afectarà aquells serveis que estan reservats per a usuaris registrats o que són objecte de contractació prèvia i que es regiran per les seves condicions específiques."
            },
            customerService: {
              title: "11. ATENCIÓ AL CLIENT I RECLAMACIONS",
              contact: "Per a interposar qualsevol queixa o reclamació davant Examenselectivitat el client haurà de dirigir-se al departament d'atenció al client bé a través de l'adreça de correu electrònic hola@sele.ai.",
              requirements: "Per a obrir una incidència/reclamació ha d'indicar-nos el número de la comanda/encarrego i el correu utilitzat a l'hora de dur a terme la comanda per qualsevol dels mitjans indicats",
              onlineDisputes: {
                title: "Resolució de litigis en línia - R.L.L.",
                text: "Segons la Resolució de litigis en línia en matèria de consum de la UE i conforme a l'Art. 14.1 del Reglament (UE) 524/2013, l'informem que la Comissió Europea facilita a tots els consumidors una plataforma de resolució de litigis en línia que es troba disponible en el següent enllaç: http://ec.europa.eu/consumers/odr/."
              }
            },
            legislation: {
              title: "12. LEGISLACIÓ APLICABLE",
              law: "Les condicions presents es regiran per la legislació espanyola vigent.",
              language: "La llengua utilitzada serà el Català."
            }
          }
        },
        es: {
          title: "Condiciones Generales",
          intro: {
            p1: "La adquisición de cualquiera de los servicios conlleva la aceptación plena y sin reservas de todas y cada una de las Condiciones Generales que se indican, sin perjuicio de la aceptación de las Condiciones Particulares que pudieran ser aplicables al adquirir determinados servicios.",
            p2: "Estas Condiciones Generales podrán ser modificadas sin notificación previa, por tanto, es recomendable leer atentamente su contenido antes de proceder a la adquisición de cualquiera de los servicios ofertados."
          },
          source: "La fuente de los exámenes son contenidos editados provenientes de la Generalitat de Catalunya. Departamento de Economía y Conocimiento. Consejo Interuniversitario de Cataluña (CIC)",
          sections: {
            identification: {
              title: "1. IDENTIFICACIÓN",
              text: "Sele.ai, en cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico, le informa que:",
              details: {
                name: "Su denominación social es: Paꜱqual Maꜱ.",
                commercial: "Su nombre comercial es: Sele.ai",
                cif: "Su CIF es: 47998845F",
                address: "Su domicilio social está en: Pg. Gracia, 118, 08008 - Barcelona, (España)"
              }
            },
            communications: {
              title: "2. COMUNICACIONES",
              text: "Para comunicarse con nosotros, ponemos a su disposición diferentes medios de contacto que detallamos a continuación:",
              contact: {
                email: "Email: hola@sele.ai",
                address: "Dirección postal: Pg. Gracia, 118, 08008 - Barcelona, (España)",
                note: "Todas las notificaciones y comunicaciones entre los usuarios y Sele.ai se considerarán eficaces, con carácter general, cuando se realicen a través de cualquier medio de los detallados anteriormente.",
                marketing: {
                  title: "Comunicaciones comerciales",
                  text: "Al aceptar estas condiciones generales, autorizas expresamente a Sele.ai para enviarte comunicaciones comerciales personalizadas por correo electrónico sobre productos y servicios que puedan ser de tu interés, basándonos en tu historial de compras y preferencias. Puedes revocar este consentimiento en cualquier momento contactando con nosotros en hola@sele.ai."
                }
              }
            },
            services: {
              title: "3. SERVICIOS",
              description: "Los servicios ofertados por Sele.ai consisten en la realización de impresión de documentos o la venta de software como servicio, con la configuración particular y características indicadas en el proceso de compra. En el caso de los dossieres, el cliente obtendrá como resultado la impresión sobre papel u otros soportes, con la entrega a domicilio en la dirección mencionada en el proceso de compra.",
              rights: {
                p1: "Sele.ai se reserva el derecho de decidir, en cada momento, los productos y/o servicios que se ofrecen a los Clientes. De esta manera, Sele.ai podrá, en cualquier momento, añadir nuevos productos y/o servicios a los ofertados actualmente.",
                p2: "Asimismo Sele.ai se reserva el derecho a retirar o dejar de ofrecer, en cualquier momento, y sin previo aviso, cualquiera de los servicios ofertados, sin que esto afecte a los servicios contratados cuya prestación no haya finalizado.",
                p3: "Todo ello sin perjuicio de que la adquisición de alguno de los servicios solo podrá hacerse mediante la suscripción o registro del USUARIO, cuyo nombre de Usuario será su correo electrónico que llevará asociada una contraseña, los cuales le identificarán y habilitarán personalmente para poder tener acceso a los servicios.",
                p4: "Una vez dentro de https://sele.ai, y para acceder a la contratación de los diferentes servicios, el Usuario deberá seguir todas las instrucciones indicadas en el proceso de compra, lo cual supondrá la lectura y aceptación de todas las condiciones generales y particulares fijadas en https://sele.ai"
              }
            },
            availability: {
              title: "4. DISPONIBILIDAD Y SUSTITUCIONES",
              access: "Para la prestación de servicios, Sele.ai, dará acceso de la siguiente forma: creando el cliente una cuenta y realizando el encargo a través de la web siguiendo los pasos indicados en el proceso de compra. El Cliente recibirá correos sobre el envío y se le informará de todos los datos necesarios para saber el estado en el que se encuentra el pedido.",
              unavailable: {
                p1: "En caso de que, por cualquier motivo, Sele.ai no pueda realizar la impresión y, por tanto, no pueda entregarse el encargo al cliente, se le informará lo antes posible y se realizará la devolución del importe pagado por este producto concreto.",
                p2: "Sele.ai no se responsabiliza de los daños o perjuicios que puedan producirse a este USUARIO como consecuencia de fallos o desconexiones en las redes de telecomunicaciones y que ocasionen suspensión, cancelación o interrupción del servicio de https://sele.ai durante la prestación del mismo."
              }
            },
            purchaseProcess: {
              title: "5. PROCESO DE COMPRA Y/O CONTRATACIÓN",
              availability: "La recepción de los pedidos y la compra online, puede realizarse en cualquier momento, las 24h. del día los 365 días del año.",
              requirements: {
                age: "Para realizar cualquier compra en https://sele.ai, es necesario que el cliente sea mayor de edad.",
                registration: "Además será necesario que el cliente se registre en https://sele.ai a través de un formulario de recogida de datos en el que se proporcione a Sele.ai la información necesaria para la contratación; datos que en cualquier caso serán veraces, exactos y completos sobre su identidad y que el cliente deberá consentir expresamente mediante la aceptación de la política de privacidad de Sele.ai."
              },
              steps: {
                title: "Los pasos a seguir para realizar la compra son:",
                list: [
                  "Añada a la cesta tantos productos como desee.",
                  "Vaya a la cesta y rellene la información necesaria para calcular el envío",
                  "Indique la forma en que desea recibir el pedido y realice el pago."
                ],
                confirmation: "Al realizar el pedido, se le enviará desde hola@sele.ai un correo con toda la información del pedido que sirve como comprobante."
              },
              billing: {
                title: "FACTURACIÓN",
                text: "Si desea recibir factura, solicítela enviando un correo electrónico a hola@sele.ai"
              }
            },
            delivery: {
              title: "6. ENTREGA",
              zones: "Las zonas geográficas de entrega son: Península, Baleares, Portugal Peninsular y envíos a Europa",
              formsAndTerms: {
                title: "FORMAS Y TÉRMINOS DE ENTREGA",
                description: "La forma de entrega se elige por el cliente en el proceso de compra. Los términos de entrega variarán según la modalidad elegida y destino.",
                schedule: "Todas las órdenes formalizadas y pagadas, que se reciban y completen antes de las 12.00 horas de un día laborable (Lunes a Viernes),",
                processing: "se imprimirán y entregarán a la agencia de transporte en el mismo día, teniendo preferencia aquellas órdenes contratadas como Servicios de Entrega EXPRESS"
              },
              highSeason: {
                title: "TEMPORADA ALTA",
                text: "En épocas de máxima demanda (inicio del curso escolar \"setiembre-octubre\", Navidad, exámenes, cambio de cuatrimestre, \"febrero\", o convocatorias de oposiciones, el tiempo de fabricación y envío puede verse incrementado en 24h. más del tipo de envío elegido, exceptuando los tipos de Envío Express, que se hace el posible para cumplir los términos."
              },
              homeDelivery: {
                title: "Envíos y entrega de órdenes \"A domicilio\"",
                schedule: "Los envíos con entrega en domicilio se realizan de Lunes a Viernes, en días laborables.",
                holidays: {
                  title: "FESTIVOS",
                  text: "Los sábados, domingos y festivos, no se realizan entregas. Festivos Locales, Regionales o Nacionales, tanto de Origen como de Destino, tampoco se realizan recogidas, ni entregas por parte del transporte, por lo que no se contabilizan como día laborable."
                },
                services: [
                  "Servicio de entrega en domicilio Península - Servicio realizado por empresas de transporte independientes.",
                  "Servicio de entrega en domicilio Baleares - Servicio realizado por empresas de transporte independientes, teniendo en cuenta las indicaciones en el apartado AGENCIAS",
                  "Para entregas en poblaciones pequeñas, distantes de la agencia de transporte, podría retrasarse *24h., en casos excepcionales."
                ]
              },
              observations: {
                title: "OBSERVACIONES SOBRE LA ENTREGA",
                agencies: {
                  title: "AGENCIAS",
                  text: "El envío de nuestros productos se realiza por agencias de transporte independientes cuya efectividad en la entrega al cliente en el tiempo acordado es superior al 93%. Los envíos se entregan en la dirección del Destinatario facilitada como <dirección de envío> esta ha de ser completa y clara, con todas las datos necesarias y exactas para el Remitente, Teléfono nacional, Contacto, dirección, núm. portal, escala, pisos, puertas, etc."
                },
                schedules: {
                  title: "HORARIOS DE ENTREGAS",
                  text: "Los horarios para la entrega en día laborable no podrán ser fijados con anterioridad, ni exigibles para el usuario."
                },
                delays: {
                  title: "RETARDOS EN LA ENTREGA",
                  text: "En caso de retardos en la entrega, el cliente en ningún caso podrá exigir la devolución íntegra o parcial de la comanda realizada o los gastos de envío. Así mismo se informa a los clientes que en épocas navideñas, campañas de venta en línea como Blackfriday, entrega en horarios no concertados, campaña de verano, por condiciones climatológicas adversas, cortes o accesos restringidos, tanto en la zona de entrega, como en cualquier de las rutas/enlaces, que el paquete sufra o requiera por llegar desde origen a destino, etc. o similar, pueden producirse retardos o demoras en la entrega."
                },
                incorrectAddress: {
                  title: "DIRECCIÓN INCOMPLETA, INCORRECTA, ABSENTE REITERADA",
                  text: "Si por motivos de dirección incompleta, incorrecta, ausente en varios intentos de entrega (2 exactamente), o rechazo del producto, se generaría devolución de mercancía, o esta tendría que ser enviada de nuevo a esta o otra dirección la <recanalización> tendrá que ser abonada por el cliente.",
                  consequences: "La imposibilidad de entregar el producto de manera definitiva por causas imputables al cliente por los motivos indicados anteriormente, y provocar un retorno a nuestras instalaciones, NO dará derecho al cliente a reclamar el importe abonado por el trabajo encomendado, salvo que demuestre que la falta de entrega se ha debido a causas ajenas a su voluntad, situación extraordinaria o fuerza mayor. El cliente tiene un plazo de 30 días desde la devolución de la encomienda, para abonar un nuevo envío y se enviará de nuevo, o pasado este tiempo, la encomienda pasará a destrucción y NO podrá reclamar importe alguno."
                }
              }
            },
            pricing: {
              title: "7. PRECIOS, GASTOS Y TRIBUTOS",
              general: "Los precios de los servicios que se recogen en https://sele.ai vendrán expresados en Euros salvo que se exprese lo contrario y estarán garantizados durante el tiempo de validez de los mismos y sea como sea el destino de la comanda.",
              details: "Los precios finales de todos los servicios, junto con sus características, ofertas, descuentos, etc. se reflejan en cada uno de los servicios que se ofrecen en https://sele.ai",
              tax: {
                included: "Los servicios tienen el IVA incluido.",
                rate: "El tipo de IVA es del 21%."
              },
              shipping: {
                info: "Gastos de envío: están informados en el sitio web, según la modalidad de entrega elegida y la cantidad de productos seleccionados.",
                free: "Envíos gratuitos para la Península para compras superiores de 4 dossiers."
              }
            },
            payment: {
              title: "8. FORMAS DE PAGO",
              creditCard: {
                title: "Tarjeta de crédito o débito",
                description: "Puede realizar el pago de manera inmediata a través de la pasarela de pago segura alimentada por la compañía Stripe.",
                refund: "Si fuera necesario realizar cualquier devolución o cancelación de la comanda, el importe se reembolsará a través de la misma tarjeta de pago (reembolso 5-10 días)."
              }
            },
            returns: {
              title: "9. POLÍTICA DE DEVOLUCIONES Y DESISTIMIENTO",
              legal: "Según la legislación española y europea (Ley General para la Defensa de los Consumidores y Usuarios y otras leyes complementarias, RD 1/2007), hay algunas pautas generales:",
              types: {
                withdrawal: {
                  title: "Droga de desistimiento",
                  description: "Si el consumidor ejerce el derecho de desistimiento (es decir, retorna el producto simplemente porque ha cambiado de opinión), los gastos de devolución son a cargo del consumidor."
                },
                defective: {
                  title: "Productos defectuosos o incorrectos",
                  description: "Si la devolución es debida a un defecto del producto o a un error en el envío. Se enviará de nuevo y todas las gastos se harán cargo de Examenselectivitat."
                }
              },
              generalInfo: {
                title: "Información general",
                deadline: "El plazo para desistir del contrato de prestación de servicios es de 14 días naturales desde la celebración del contrato, devent el usuario poner en conocimiento de Examenselectivitat su deseo de ejercitar tal derecho, la cual podrá hacer emplenando el formulario que se encuentra al final de este documento.",
                suspension: "El ejercicio del derecho de desistimiento da lugar a la suspensión del servicio.",
                confirmation: "Examenselectivitat comunicará al consumidor en soporte durador un justificante de recepción de este desistimiento.",
                refund: "El desistimiento implica que, Examenselectivitat procederá a la devolución del importe ya abonado por el cliente en un plazo máximo de 14 días naturales, siguiendo el mismo procedimiento elegido por el cliente para su abonamento (siempre que el servicio contratado no esté dentro de las excepciones indicadas anteriormente).",
                contact: "Una vez recibamos su solicitud nos pondremos en contacto con el cliente para indicarle los detalles de la devolución."
              },
              orderCancellation: {
                title: "ANULACIÓN DE ÓRDENES Y ÓRDENES EN PROCESO",
                timeLimit: "Puede anular la comanda mientras la recepción de la comanda y la comunicación de anulación no hagan transcurrido más de 3 horas",
                process: "Se puede solicitar por correo a hola@sele.ai y el reembolso se devolverá a la misma tarjeta bancaria con la que se había realizado el pago.",
                restriction: "No se podrá anular la comanda, si ha recibido un correo electrónico expresando que su comanda está de camino."
              }
            },
            responsibility: {
              title: "10. RESPONSABILIDAD Y EXONERACIÓN DE RESPONSABILIDAD",
              privacy: "Así mismo Examenselectivitat cumple con la normativa vigente de protección de datos. Dispone de información ampliada en nuestra Política de Privacidad",
              serviceChanges: "Examenselectivitat podrá suspender, retirar o cancelar parcial o totalmente los servicios sujetos a contratación en cualquier momento y sin necesidad de previo aviso. La previsión anterior no afectará aquellos servicios que están reservados para usuarios registrados o que son objeto de contratación previa y que se regirán por sus condiciones específicas."
            },
            customerService: {
              title: "11. ATENCIÓN AL CLIENTE Y RECLAMACIONES",
              contact: "Para interponer cualquier queja o reclamación ante Examenselectivitat el cliente tendrá que dirigirse al departamento de atención al cliente bien a través de la dirección de correo electrónico hola@sele.ai.",
              requirements: "Para abrir una incidencia/reclamación hay que indicarnos el número de la comanda/encargo y el correo utilizado al realizar la comanda por cualquier de los medios indicados",
              onlineDisputes: {
                title: "Resolución de litigios en línea - R.L.L.",
                text: "Según la Resolución de litigios en línea en materia de consumo de la UE y conforme al Art. 14.1 del Reglamento (UE) 524/2013, l'informem que la Comisión Europea facilita a todos los consumidores una plataforma de resolución de litigios en línea que se encuentra disponible en el siguiente enlace: http://ec.europa.eu/consumers/odr."
              }
            },
            legislation: {
              title: "12. LEGISLACIÓN APLICABLE",
              law: "Las condiciones presentes se regirán por la legislación española vigente.",
              language: "La lengua utilizada será el castellano."
            }
          }
        }
      }
    }
  },
  methods: {
    $t(key) {
      const lang = this.$root.currentLanguage || 'ca'
      const translation = key.split('.').reduce((obj, i) => obj && obj[i], this.translations[lang])
      return translation || key
    }
  },
  watch: {
    '$root.currentLanguage'() {
      // Si necesites hacer alguna cosa cuando cambie el idioma
    }
  }
}
</script>

<style scoped>

</style>
